<!--
 Name: RferMCI.vue
 Description: View to display the refermci page.
 Created by: Andjelka Popovic on 2025-07-16
 Last edited by: Andjelka Popovic on 2026-01-15
-->

<template>
  <v-container fluid class="pa-0 full-width">
    <!-- hero graphic -->
    <div class="bg-orange-primary" :style="{ height: '800px' }">
      <v-row no-gutters>
        <v-col cols="12">
          <p class="hero-title text-white pa-4 pa-sm-0
        ml-sm-12 pl-sm-12 mt-sm-12 pt-sm-12">
            Willkommen im Fachportal von neotivCare - <br>
            Ihr Leitfaden zur Anwendung der <b>neotivCare-App</b> im Rahmen <br>
            der <b>Refer-MCI-Studie</b>
          </p>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <!-- hero text -->

        <v-col cols="12" cols-sm="11" md="5" offset="0" offset-sm="1" class="pa-4 pa-sm-0">
          <v-row class="mt-2 pt-2" no-gutters>
            <v-col cols="11">
              <p class="hero-text text-white mt-2 mt-sm-12">Hier finden Sie alle wichtigen Informationen zur
                Refer-MCI-Studie und der digitalen Selbsttestung der kognitiven Leistungsfähigkeit mit Hilfe der <b>neotivCare-App</b>.
              </p>
              <v-btn
                variant="flat"
                size="large"
                @click="scrollToSection('.cognitiveTests')"
                class="hero-text mt-4 mt-sm-12 mr-4"
              >
                Drei Arten kognitiver Aufgaben
              </v-btn>
              <v-btn
                variant="flat"
                size="large"
                @click="scrollToSection('.specificInfo')"
                class="hero-text mt-4 mt-sm-12"
              >
                Ablauf der Studie
              </v-btn>
              <v-btn
                variant="flat"
                size="large"
                @click="scrollToSection('.frequent-questions')"
                class="hero-text mt-4 mt-sm-12"
              >
                Fragen zur neotivCare-App
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <!-- v-col in required to make it work on the smaller devices in safari -->
        <!-- hidden-sm-and-down doesn't work with d-flex -->
        <v-col
          v-if="$vuetify.display.mdAndUp"
          cols="6"
          class="justify-end d-flex"
        >
          <v-img eager
                 :src="require('@/assets/images/studies/herosection_app.png')" cover height="550px"
          />
        </v-col>
      </v-row>
    </div>

    <!-- Study Infos -->
    <section class="studyInfos bg-white">
      <v-container>
        <h1 class="text-left my-6">
          Allgemeine Studieninformationen
          <v-divider></v-divider>
        </h1>
        <v-row class="mt-6" no-gutters>
          <p class="text-justify">
            Die Refer-MCI-Studie untersucht, wie Patienten und Patientinnen mit Verdacht auf eine leichte kognitive
            Beeinträchtigung (MCI) effizienter und schneller von Facharztpraxen an Gedächtnisambulanzen überwiesen
            werden können. Dabei soll bewertet werden, ob eine unbeaufsichtigte digitale Selbsttestung der kognitiven
            Leistungsfähigkeit, die mit der neotivCare-App durchgeführt wird, als Hilfsmittel in der fachärztlichen
            Versorgung die Überweisungen bei MCI-Verdacht verbessern kann.
          </p>
          <br>
          <p class="mb-2 text-justify">
            Die App neotivCare ist ein CE-zertifiziertes Medizinprodukt der Risikoklasse IIa nach MDR. Die
            App selbst wird im Rahmen der Studie nicht geprüft, sondern ausschließlich als zweckgebundenes Werkzeug
            eingesetzt, wobei eine Konformitätserklärung bereits vorliegt.
          </p>
        </v-row>
        <v-row class="mt-6" no-gutters>
          <h2 class="text-left my-6">
            Über neotiv & neotivCare
            <v-divider></v-divider>
          </h2>
          <p class="text-justify">
            Die neotiv GmbH ist ein Unternehmen, das 2017 aus der Otto-von-Guericke-Universität Magdeburg
            hervorgegangen ist und seit Gründung eng mit dem Deutschen Zentrum für Neurodegenerative Erkrankungen
            (DZNE) zusammenarbeitet. Ziel von neotiv ist es, die Erfassung und Überwachung kognitiver Veränderungen
            mit Hilfe digitaler Lösungen im Verlauf von neurodegenerativen Erkrankungen zu ermöglichen.
          </p>
          <br>
          <p class="mb-2 text-justify">
            In der neotivCare-App sind drei verschiedene Gedächtnistests implementiert, die jeweils viermal durchgeführt
            werden in einem Zeitraum von etwa 12 Wochen. Die drei Gedächtnistests zielen auf spezifische Hirnregionen
            ab, die früh im Verlauf der Alzheimer-Pathologie betroffen sind und erfassen Änderungen des episodischen
            Gedächtnisses. Sie wurden so konzipiert, dass sie von Patient:innen selbstständig und flexibel im häuslichen
            Umfeld auf ihren eigenen Endgeräten durchgeführt werden können. Die neotivCare-Testung dient der Erkennung
            einer kognitiven Beeinträchtigung im MCI-Stadium und wurde anhand der PACC5-Testbatterie validiert, dabei
            wurde mit einem Cut-off von -0,41 eine Sensitivität von 85% und eine Spezifität von 75% erzielt (Berron et
            al., 2024, npj Digital Medicine). Die Validität und Sensitivität dieser Tests wurden bereits in einer
            Vielzahl von wissenschaftlichen Studien untersucht, die in renommierten Fachzeitschriften publiziert wurden:
            <a href="https://neotiv.com/de/publications" target="_blank">neotiv.com/de/publications</a>.
          </p>
        </v-row>
      </v-container>
    </section>

    <!-- Types of cognitive tests -->
    <section class="">
      <v-container>
        <h2 class="text-left my-6 anchor-section cognitiveTests">
          Aufbau der drei spezialisierten Tests
        </h2>
        <v-row>
          <v-col cols="12" md="7" class="align-self-center">
            <p><b><span class="text-orange-primary">Unterschiede erkennen</span> – Gedächtnispräzision</b></p>
            <p class="">Bilder von Objekten und Räumen werden wiederholt präsentiert, wobei Patient:innen entscheiden, ob es sich um ein identisches oder ein leicht verändertes Bild handelt.</p>
            <ul>
              <li class="mb-4">
                <i>Kognitiver Mechanismus: Mustertrennung und Repräsentation von Objekten und Szenen</i>
              </li>
            </ul>
          </v-col>
          <v-col cols="12" md="5" class="align-self-center">
            <v-img eager
                   :src="require('@/assets/images/studies/test3.png')" max-width="100%" height="auto"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="7" class="align-self-center">
            <p><b><span class="text-orange-primary">Gegenstände merken</span> – räumliches Assoziationsgedächtnis</b></p>
            <p>
              Patienten:innen sehen Räume mit jeweils zwei Objekten; nach 30 Minuten wird das Langzeitgedächtnis für die Objektposition im leeren Raum geprüft.
            </p>
            <ul>
              <li class="mb-4">
                <i>Kognitiver Mechanismus: prüft assoziatives Erinnern und Mustervervollständigung</i>
              </li>
            </ul>
          </v-col>
          <v-col cols="12" md="5" class="align-self-center">
            <v-img eager
                   :src="require('@/assets/images/studies/test2.png')" max-width="100%" height="auto"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="7" class="align-self-center">
            <p><b><span class="text-orange-primary">Drinnen & Draußen</span> – fotografisches Gedächtnis</b></p>
            <p class="mb-4">
              In der Lernphase werden fotografische Bilder präsentiert. Nach 65 Minuten müssen Patient:innen gelernte Bilder von neuen Bildern unterscheiden.
            </p>
            <ul class="mb-4">
              <li>
                <i> Kognitiver Mechanismus: Misst Wiedererkennung und Vertrautheit</i>
              </li>
            </ul>
          </v-col>
          <v-col cols="12" md="5" class="align-self-center">
            <v-img eager
                   :src="require('@/assets/images/studies/test1.png')" max-width="100%" height="auto"
            />
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Composite score and Findings letter -->
    <section>
      <v-container>
        <h2 class="text-left my-6 findingsLetter anchor-section">
          Composite Score & Befundbrief
          <v-divider></v-divider>
        </h2>

        <p>
          Der Befundbrief, der automatisiert in der App abgerufen werden kann, liefert ein aussagekräftiges Bild über
          die Gedächtnisleistung von Patient:innen. Er enthält das Gesamtergebnis (Composite Score) sowie
          dessen Einordnung im Vergleich zu der Referenzgruppe bestehend aus 60–80-jährigen Personen mit und ohne
          diagnostizierte kognitive Einschränkung (siehe Bild). Zusätzlich werden alle Einzeltestergebnisse aufgeführt,
          inklusive der Selbsteinschätzung der Patient:innen zu ihrer Testleistung und Konzentration. Für jeden Test
          kann zudem vermerkt werden, ob besondere Umstände (z. B. Krankheit, Müdigkeit, familiäre Belastung) die
          Leistung beeinflusst haben.
        </p>
        <p>
          Ein Composite Score kann berechnet werden, sobald mindestens 2 valide Testergebnisse pro Test vorliegen. Nach
          Abschluss aller Tests, wird der endgültige Composite Score im Befundbrief angezeigt. Zur Einordnung
          unterstützt
          ein Entscheidungsbaum (siehe Bild).
        </p>
        <p>
          Der Befundbrief kann von den Patient:innen selbstständig in der App erstellt werden und wird den Patient:innen
          per E-Mail zugeschickt. Er lässt sich zur Besprechung in der Praxis per E-Mail weiterleiten oder ausgedruckt
          von den Patient:innen zur Befundbesprechung in der Praxis mitgebracht werden.
        </p>
        <p>
          Der Befundbrief wird zusätzlich von den Patient:innen im Rahmen der weiteren Untersuchungen im Studienzentren
          zur Visite mitgeführt.
        </p>
        <v-img eager class="img-border mb-4"
               :src="require('@/assets/images/studies/findingsLetter.jpg')" max-width="100%" height="auto"
        />
        <v-img eager class="ma-auto"
               :src="require('@/assets/images/studies/decisionTree.jpg')" max-width="100%" height="auto"
        />
      </v-container>
    </section>

    <!-- Study Procedure -->
    <section class="bg-light-orange specificInfo anchor-section">
      <v-container>
        <h1 class="text-left my-6">
          Ablauf der Studie
          <v-divider></v-divider>
        </h1>

        <h2 class="mb-4">
          Genereller Studienablauf
          <v-divider></v-divider>
        </h2>

        <v-img eager class="ma-auto mb-4"
               :src="require('@/assets/images/refermci/Genereller_Studienablauf.png')" max-width="100%"
               height="auto"
        />

        <p><b>1. Erster Termin in der niedergelassenen Praxis</b> – <br>
          Patient:in wird in die Studie eingeschlossen. Es wird die Einwilligungserklärung unterschrieben und die
          Routineuntersuchung durchgeführt (inkl. MoCA Testung).</p>

        <p><b> 2. Heimtestung mit neotivCare & Fragebogen 1 </b> – <br>
          Patient:in führt im eigenen zuhause die Testung mit der neotivCare-App durch und füllt in dieser Zeit den
          Fragebogen 1 aus.</p>

        <p><b>3. Zweiter Termin zur Befundbesprechung in der Praxis</b>– <br>
          Arzt bzw. Ärztin bespricht die Ergebnisse aus der Routineuntersuchung und der neotivCare Testung sowie die
          Überweisungsentscheidung. Am Ende übergibt der Arzt bzw. die Ärztin alle relevanten Studiendokumente an den
          Patienten bzw. die Patientin.</p>

        <p><b>4. Fragebogen 2 nach der Befundbesprechung</b> – <br>
          Patient:in füllt nach der Befundbesprechung im eigenen zuhause den Fragebogen 2 aus zum Thema
          Nutzerfreundlichkeit der neotivCare App.</p>

        <p><b>5. Termin in der Gedächtnisambulanz</b> – <br>
          Patient:in besucht die Gedächtnisambulanz zur finalen Untersuchung. Es wird Blut entnommen zur
          Biomarkerbestimmung und alle Studiendokumente verbleiben in der Gedächtnisambulanz.</p>

        <p>
          Weitere Informationen zu den einzelnen Studienschritten finden Sie im Folgenden. Bei Fragen wenden Sie sich
          gerne direkt an den Studienleiter bei neotiv:
          Dr. Hannes Voßfeldt (Email: <a href="mailto:hannes.vossfeldt@neotiv.com">hannes.vossfeldt@neotiv.com</a>;
          Telefon:
          <a href="tel:01711792278">0171 179 2278</a>)
        </p>

        <h2 class="mb-4">
          Detaillierte Informationen für die Studie
          <v-divider></v-divider>
        </h2>

        <v-expansion-panels>
          <v-expansion-panel
            title="1. Erster Termin in der niedergelassenen Praxis"
          >
            <v-expansion-panel-text>
              <p><b>Übersicht der Aktivitäten beim 1. Termin in der niedergelassenen Praxis: </b></p>
              <ol class="mb-4">
                <li>Studieneinschluss von geeigneten Patient:innen</li>
                <li>Einwilligungserklärung (Arztpraxis)</li>
                <li>Routineuntersuchung (inkl. MoCA Testung)</li>
                <li>Aushändigen der Patientenmappe</li>
                <li>Dokumentation der Untersuchungsergebnisse</li>
                <li>Einführung der Patient:innen in neotivCare</li>
                <li>Folgetermin vereinbaren</li>
              </ol>


              <h2 class="mb-4">
                1. Studieneinschluss: Wer kann an der Refer-MCI Studie teilnehmen?
                <v-divider></v-divider>
              </h2>

              <p><u>Einschlusskriterien:</u></p>
              <ul class="mb-4">
                <li>Personen im Alter von 60 bis 80 Jahren</li>
                <li>wahrgenommene oder vermutete Gedächtnisprobleme</li>
                <li>geeignet für eine eingehende Diagnostik</li>
              </ul>

              <p> Voraussetzungen sind die unterzeichnete Einwilligung in die Studie sowie ausreichende
                Deutschkenntnisse. </p>

              <p><u>Ausschlusskriterien:</u></p>

              <ul>
                <li>Ursachen für kognitive Beeinträchtigungen, die nicht mit neurodegenerativen Prozessen
                  zusammenhängen
                </li>
                <li>Verdacht auf manifeste Demenz</li>
                <li>Unfähigkeit zur Smartphone- oder Tablet-Nutzung</li>
                <li>aktuelle psychische oder neurologische Erkrankungen sowie Seh- oder Hörstörungen, die die
                  Durchführung der
                  Untersuchungen beeinträchtigen
                </li>
              </ul>

              <h2 class="mb-4">
                2. Einwilligungserklärung (Arztpraxis)
                <v-divider></v-divider>
              </h2>
              <ul class="mb-4">
                <li>„Einwilligungserklärung (Arztpraxis)“ ist für die Studienteilnahme von Patient:in sowie Arzt bzw.
                  Ärztin in der Praxis zu unterschreiben
                </li>
                <li> 2 Exemplare sind vorhanden:<br>
                  Ein Exemplar (mit Freischaltcode versehen) verbleibt in der Praxis und das andere kann in der
                  Patientenmappe aufbewahrt werden und soll ins Referenzzentrum mitgeführt werden (gelber Punkt darauf
                  versehen)
                </li>
              </ul>


              <h2 class="mb-4">
                3. Routineuntersuchung – sollte folgende Inhalte abdecken:
                <v-divider></v-divider>
              </h2>
              <ul class="mb-4">
                <li>Medizinische Anamnese inkl. Medikation</li>
                <li>Demografie</li>
                <li>SCD-Q (Subjective Cognitive Decline Questionnaire) oder entsprechende Einschätzung der kognitiven
                  Probleme
                </li>
                <li>Standard neurologische, körperliche Untersuchung (siehe Dokumentationsbogen)</li>
                <li>MoCA Testung</li>
                <li>Die Teilnehmer:innen an dieser Studie erhalten seitens der niedergelassenen Ärzt:innen eine
                  schriftliche
                  standardisierte Zusammenfassung der Ergebnisse der ambulanten Regelversorgung, um diese zum Termin im
                  Referenzzentrum/Gedächtnisambulanz mitzubringen. Diese sollte auf jeden Fall das Ergebnis der MoCA
                  Testung
                  enthalten.
                </li>
              </ul>

              <h2 class="mb-4">
                4. Aushändigen der Patientenmappe inkl. Fragebögen für zu Hause
                <v-divider></v-divider>
              </h2>

              <p> Die Patient:innen erhalten eine Mappe mit Informationen sowie relevanten Dokumenten zur Studie und zur
                neotivCare-App. Diese enthält eine Patientenbroschüre, den Freischaltcode für die App – von neotiv
                bereits auf
                allen relevanten Materialien zur Identifikation angebracht – sowie Patientenfragebögen in Papierform.
                Details
                zu den Patientenfragebögen finden Sie im nächsten Abschnitt unter „Heimtestung mit neotivCare“.</p>

              <h2 class="mb-4">
                5. Dokumentation der Untersuchungsergebnisse
                <v-divider></v-divider>
              </h2>

              <p><b>Dokumentationsbögen für Sie als Arzt bzw. Ärztin (enthalten in der Patientenmappe)</b></p>
              <p> Dieser Bogen enthält die Überweisungsentscheidung an die jeweilige Gedächtnisambulanz nach
                Routineuntersuchung. Diese Dokumentation dient der Erfassung Ihrer üblichen Versorgung bei
                Gedächtnisstörungen
                sowie Ihrer Überweisungsentscheidung – mit und ohne Berücksichtigung des Ergebnisses aus der
                neotivCare-App.
                Sie finden die Bögen innerhalb der Patientenmappe.</p>

              <table class="mb-4">
                <thead>
                <tr>
                  <th colspan="2">
                    Dokumentationsbögen für Sie als Arzt bzw. Ärztin
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>Auszufüllen nach der Routineuntersuchung</td>
                  <td>Auszufüllen nach der Befundbesprechung inklusive <br> neotivCare Ergebnis</td>
                </tr>
                <tr>
                  <td>Anamnese & Diagnostik (1)</td>
                  <td rowspan="2">Anamnese & Diagnostik (2)</td>
                </tr>
                <tr>
                  <td>S3 Leitlinien – Routineversorgung</td>
                </tr>
                </tbody>
              </table>

              <p> Die Bögen können vor und nach dem Ausfüllen bei den Patient:innen in der Patientenmappe verbleiben und
                zu
                den jeweiligen Visiten mitgebracht werden. Wenn Sie eine andere Ablage bevorzugen, ist das natürlich
                möglich.
                Nach den beiden Visiten sollen die Bögen von den Patient:innen in das Referenzzentrum mitgenommen
                werden. </p>

              <h2 class="mb-4">
                6. neotivCare Download und Einweisung
                <v-divider></v-divider>
              </h2>

              <p> Einweisung der Patienten und Patientinnen in der Praxis, inkl. Download der App und Eingabe des
                Aktivierungscodes - <a @click="scrollToSection('.Instructions')" class="anchor-link">Hinweise zur
                  korrekten Durchführung der
                  Tests
                  mit neotivCare</a></p>

              <ul class="mb-4">
                <li>In der Praxis wird der Freischaltcode mit dem Gerät des Patienten bzw. der Patientin eingelöst und
                  die
                  Registrierung durchgeführt, sodass die Patient:innen direkt mit der neotivCare-Testung starten können
                  <ul>
                    <li> Das Praxispersonal wird gebeten die Patient:innen in diesem Schritt zu unterstützen und in die
                      neotivCare-App einzuweisen
                    </li>
                  </ul>
                </li>
                <li> Patienten und Patientinnen führen über 12 Wochen hinweg jeweils einen Test pro Woche durch</li>
              </ul>

              <h2 class="mb-4">
                7. Vereinbarung eines Folgetermins
                <v-divider></v-divider>
              </h2>

              <p> Vor Verlassen der Praxis wird ein Termin zur Wiedervorstellung nach 12 bis 14 Wochen vereinbart. Zum
                Befundgespräch bringt der Patient bzw. die Patientin den Befundbrief mit – weitere Infos unter
                „Heimtestung
                mit neotivCare“.</p>

            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            title="2. Heimtestung mit neotivCare & Fragebogen 1"
          >
            <v-expansion-panel-text>
              <p><b>Übersicht der Aktivitäten:</b></p>
              <ul class="mb-4">
                <li>Durchführung der neotivCare Testung im eigenen zuhause</li>
                <li>Fragebogen 1 zum Ausfüllen und Abschicken</li>
                <li>Befundbrief generieren und zur Befundbesprechung mitbringen</li>
              </ul>

              <h2 class="mb-4">
                1. Durchführung der neotivCare Testung im eigenen Zuhause
                <v-divider></v-divider>
              </h2>

              <p> Die Patient:innen führen im häuslichen Umfeld die neotivCare Tests durch. Für Fragen zur
                Testdurchführung siehe <a @click="scrollToSection('.Instructions')" class="anchor-link">Hinweise zur
                  korrekten Durchführung
                  der Tests mit neotivCare</a>.</p>

              <h2 class="mb-4">
                2. Fragebogen 1 zum Ausfüllen und Abschicken:
                <v-divider></v-divider>
              </h2>
              <p> Die Patientenbroschüre enthält zwei Patientenfragebögen in Papierform, die sich aus den folgenden
                Einzelfragebögen zusammensetzen:</p>

              <table class="mb-4">
                <thead>
                <tr>
                  <th colspan="2">Fragebögen für Patient:innen</th>
                </tr>
                <tr>
                  <td>Fragebogen 1:
                    Während der Nutzung von neotivCare auszufüllen
                  </td>
                  <td>Fragebogen 2:
                    Nach Nutzung und der Befundbesprechung von neotivCare auszufüllen
                  </td>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>Soziodemographie</td>
                  <td rowspan="6">Fragebogen zu den Erfahrungen der Patient:innen mit der neotivCare-App und der
                    Befundbesprechung
                  </td>
                </tr>
                <tr>
                  <td>MCI-HLA scale -
                    Fragebogen zum medizinischen Verständnis
                  </td>
                </tr>
                <tr>
                  <td>PPRS-F -
                    wahrgenommen Arzt-Patientenbeziehung zum behandelnden Facharzt und behandelnden Hausarzt
                  </td>
                </tr>
                <tr>
                  <td>WHO-QOL-BREF –
                    Fragebogen zur gesundheitsunabhängigen Lebensqualität
                  </td>
                </tr>
                <tr>
                  <td>FIMA –
                    Inanspruchnahme von gesundheitlichen Leistungen
                  </td>
                </tr>
                <tr>
                  <td>Duke Social Support Index DSSI - Fragebogen zur wahrgenommenen sozialen Unterstützung</td>
                </tr>
                </tbody>
              </table>

              <p> Die beiden Fragebögen werden von den Patient:innen an unterschiedlichen Zeitpunkten zu Hause
                ausgefüllt - <b>einen während der Nutzung von neotivCare und einen nach der Befundbesprechung</b>.
                Während der Heimtestung mit neotivCare soll der <b>gesamte Fragebogen 1</b> ausgefüllt und zur
                Auswertung postalisch verschickt werden. </p>

              <p> Zwei frankierte und adressierte Rückumschlag liegen den Unterlagen bei, sodass die Patienten und
                Patientinnen beide Fragebögen nach Ausfüllen eigenständig an das auswertende Institut für Community
                Medicine Greifswald senden können. </p>

              <p class="mb-0">Die Kontaktdaten sind:</p>
              <p class="mb-0">Prof. Dr. Jochen René Thyrian</p>
              <p class="mb-0">Institut für Community Medicine</p>
              <p class="mb-0">Ellernholzstr. 1-2</p>
              <p class="mb-0">17489 Greifswald</p>
              <p><a href="mailto:rene.thyrian@dzne.de">rene.thyrian@dzne.de</a></p>

              <h2 class="mb-4">
                3. Befundbrief generieren und zur Befundbesprechung mitbringe
                <v-divider></v-divider>
              </h2>

              <p> Zum Befundgespräch bringt der Patient bzw. die Patientin den Befundbrief selbst in ausgedruckter Form
                mit oder sendet ihn digital per E-Mail an die Praxis – für detaillierte Infos dazu siehe
                <a @click="scrollToSection('.frequent-questions')" class="anchor-link">Wie generiere ich den
                  Befundbrief</a></p>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            title="3. Zweiter Termin zur Befundbesprechung in der Praxis"
          >
            <v-expansion-panel-text>
              <p class="mb-0"><b>Übersicht der Aktivitäten: </b></p>
              <ol>
                <li>Befundbesprechung</li>
                <li>Abgabe aller Studiendokumente an den Patienten bzw. die Patientin</li>
              </ol>

              <h2 class="mb-4">
                1. Befundbesprechung: Ergebnisse der Routineuntersuchung & neotivCare
                <v-divider></v-divider>
              </h2>
              <p> Bei der Befundbesprechung werden wie gewohnt die Ergebnisse aus der Routineuntersuchung mit dem
                Patienten bzw. die Patientin geteilt. Außerdem wird der neotivCare Befundbrief besprochen. </p>

              <ul class="mb-4">
                <li><b>Überweisungsentscheidung:</b> Alle Patient:innen werden im Rahmen der Studie, unabhängig von
                  Ihrer klinischen Einschätzung oder der durch neotivCare unterstützten Entscheidung, an eine
                  entsprechende Gedächtnisambulanz überwiesen. Bei diesem 2. Termin haben Sie die Möglichkeit, mit der
                  Patientin bzw. dem Patienten Ihre Überweisungsentscheidung zu besprechen.
                  <ul>
                    <li><b>Besprechung des Befunds der neotivCare-Testung</b>: Das Ergebnis der neotivCare Testung
                      sollte mit den Patient:innen besprochen werden. Hier finden Sie die Informationen zum
                      <a @click="scrollToSection('.findingsLetter')" class="anchor-link">Composite Score &
                        Befundbrief</a>

                    </li>
                  </ul>
                </li>
              </ul>

              <h2 class="mb-4">
                2. Abgabe aller Studiendokumente an die Patient:innen
                <v-divider></v-divider>
              </h2>
              <p>Am Ende des Termins übergibt der Arzt bzw. die Ärztin alle relevanten Studiendokumente an den
                Patienten/die
                Patientin. Diese werden von den Patient:innen dann zum Termin im Referenzzentrum/Gedächtnisambulanz
                mitgebracht und dort abgegeben. Die Dokumente sind folgende: </p>

              <ul class="mb-4">
                <li> Unterschriebene Einwilligungserklärungen aus der niedergelassenen Praxis</li>
                <li> Ausgefüllter Dokumentationsbogen (S3 Leitlinien – Routineversorgung)</li>
                <li>Ausgefüllter Bogen zur Anamnese & Diagnostik (1)</li>
                <li>Schriftliche Zusammenfassung der Ergebnisse der Routineuntersuchung mindestens das Resultat der MoCA
                  Testung
                  <ul>
                    <li><b>Ergebnis der MoCA Testung</b> (z.B. den ausgefüllten Testbogen) muss von den
                      Patient:innen zur Gedächtnisambulanz mitgeführt werden
                    </li>
                    <li>Eine schriftliche Zusammenfassung aller Testergebnisse kann formlos und freiwillig erfolgen</li>
                  </ul>
                </li>
                <li>Ausgefüllter Bogen zur Anamnese & Diagnostik (2)</li>
                <li> Befundbrief der neotivCare Testung</li>
              </ul>

              <p><b> Information: <br>
                Am Ende der Studie - Abschlussfragebogen zur neotivCare Nutzung für die Ärzte und Ärztinnen</b></p>
              <p> Am Ende der Refer-MCI Studie bekommen Sie als Facharzt bzw. Fachärztin einen letzten Fragebogen, der
                Ihre
                Eindrücke zur Nutzung von neotivCare im Rahmen der Studie abfragt. Dieser Fragebogen wird einmalig von
                Ihnen beantwortet und wird Ihnen noch gesondert zugeschickt.</p>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            title="4. Fragebogen 2 nach der Befundbesprechung"
          >
            <v-expansion-panel-text>
              <p class="mb-0"><b>Übersicht der Aktivitäten: </b></p>
              <ol class="mb-4">
                <li> Ausfüllen und Verschicken des Fragebogen 2</li>
                <li> Mitbringen aller Studiendokumente zum Termin ins Referenzzentrum/Gedächtnisambulanz</li>
              </ol>

              <h2 class="mb-4">
                1. Fragebogen 2 zum Ausfüllen für zu Hause:
                <v-divider></v-divider>
              </h2>
              <p> Die Patientenmappe enthält einen zweiten Patientenfragebögen in Papierform bezüglich der <b>neotivCare-Nutzung
                durch die Patient:innen</b>. Dieser <b>Fragebogen 2 soll nach der Befundbesprechung zu Haue ausgefüllt
                und</b> ebenfalls eigenständig an das auswertende Institut für Community Medicine Greifswald <b>geschickt
                werden</b>. Dazu liegt der Patientenmappe ein zweiter frankierter und adressierter Rückumschlag an
                dieselbe Adresse des auswertenden Instituts für Community Medicine Greifswald vor. </p>

              <h2 class="mb-4">
                2. Zum folgenden Termin in der Gedächtnisambulanz sollen die Patient:innen alle
                Studiendokumente in ihrer Patientenmappe mitbringen!
                <v-divider></v-divider>
              </h2>

            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            title="5. Termin im Referenzzentrum/Gedächtnisambulanz"
          >
            <v-expansion-panel-text>
              <p class="mb-0"><b>Übersicht der Aktivitäten beim 1. Termin im Referenzzentrum:</b></p>
              <ol class="mb-4">
                <li>Einwilligungserklärung (Gedächtnisambulanz)</li>
                <li>Standarduntersuchung</li>
                <li>Blutabnahme zur Blutbiomarkerbestimmung</li>
                <li>Abgabe aller Studienmaterialien der Patient:innen an das Referenzzentrum</li>
              </ol>

              <h2 class="mb-4">
                1. Einwilligung der teilnehmenden Patienten und Patientinnen:
                <v-divider></v-divider>
              </h2>
              <p> In den Referenzzentren (Gedächtnisambulanz) wird eine weitere „Einwilligungserklärung
                (Gedächtnisambulanz)“ zur Durchführung und Nutzung der Studiendaten sowie für die Abnahme des
                Biomaterial für Forschungszwecke eingeholt. </p>

              <h2 class="mb-4">
                2. Standarduntersuchungen
                <v-divider></v-divider>
              </h2>
              <table class="mb-4">
                <thead>
                <tr>
                  <th>Ärztliche Untersuchung</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>Demografie</td>
                </tr>
                <tr>
                  <td>Medizinische Anamnese</td>
                </tr>
                <tr>
                  <td>Subjektive kognitive Verschlechterung: Anamnese Kognition (Ana-Kog)</td>
                </tr>
                <tr>
                  <td>Globale Kognition: Mini-Mental Status Examination (MMSE)</td>
                </tr>
                <tr>
                  <td><b>Neuropsychologische Testung</b></td>
                </tr>
                <tr>
                  <td>Logical Memory A</td>
                </tr>
                <tr>
                  <td>CERAD +</td>
                </tr>
                <tr>
                  <td>Free and Cued Selective Reminding Test (FCSRT)</td>
                </tr>
                <tr>
                  <td><b>Self-Rating-Fragebögen</b></td>
                </tr>
                <tr>
                  <td>Geriatric Depression Scale (GDS)</td>
                </tr>
                <tr>
                  <td>WHO-QOL OLD (Quality of Life)</td>
                </tr>
                <tr>
                  <td>FAQ</td>
                </tr>
                </tbody>
              </table>

              <h2 class="mb-4">
                3. Blutabnahme für die Biomarkerbestimmung
                <v-divider></v-divider>
              </h2>

              <p>In der Gedächtnisambulanz findet eine Blutabnahme statt: </p>
              <ul>
                <li>
                  Blutabnahme
                  <ul>
                    <li>17 ml EDTA-Röhrchen</li>
                    <li>Kapillarblut</li>
                  </ul>
                </li>
              </ul>
              <p>Das entnommene Blut wird zur Analyse bezüglich des Blutbiomarkers pTau217 sowie der Bestimmung des
                ApoE-Genotyps an das folgende Zentrum geschickt:</p>
              <p><i>Adresse des Analysezentrums folgt</i></p>

              <h2 class="mb-4">
                4. Abgabe aller Studienmaterialien der Patient:innen in der Gedächtnisambulanz
                <v-divider></v-divider>
              </h2>
              <p>Alle Materialien müssen in der Gedächtnisambulanz verbleiben und in die entsprechenden Daten in die
                Klinische Forschungsplattform des DZNE eingetragen werden: </p>
              <ul>
                <li>Unterschriebene Einwilligungserklärungen aus der niedergelassenen Praxis und der
                  Gedächtnisambulanz
                </li>
                <li>Ausgefüllter Dokumentationsbogen aus der niedergelassenen Praxis (S3 Leitlinien –
                  Routineversorgung)
                </li>
                <li>Ausgefüllter Bogen zur Anamnese & Diagnostik (1)</li>
                <li>Schriftliche Zusammenfassung der Ergebnisse der Routineuntersuchung inklusive Resultat der MoCA
                  Testung
                </li>
                <li>Ausgefüllter Bogen zur Anamnese & Diagnostik (2)</li>
                <li>Befundbrief der neotivCare Testung</li>
              </ul>

              <p class="mb-0"><b></b></p>
              <p></p>

              <p class="mb-0"><b></b></p>
              <p></p>

              <p class="mb-0"><b></b></p>
              <p></p>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

      </v-container>
    </section>

    <section>
      <v-container>
        <h1 class="text-left my-6 Instructions anchor-section">Hinweise zur korrekten Durchführung der Tests in
          neotivCare
          <v-divider></v-divider>
        </h1>

        <p> Um aussagekräftige Daten zu generieren, ist es wichtig, dass alle Patient:innen die Tests unter
          optimalen Bedingungen durchführen. Das Praxis-Personal sollte daher mit den wichtigsten Aspekten der
          Testdurchführung vertraut sein, auch um eine einheitliche Einweisung zu gewährleisten.</p>

        <p class=mb-0> Konkret unterstützen das Praxispersonal die Patient:innen zu Beginn der Studie bei der
          Installation der App
          und der Registrierung in der App. Zudem sollten den Patient:innen folgende Hinweise bei der Einweisung
          vermittelt werden:</p>

        <v-row class="ma-1" no-gutters>
          <!-- Left Column -->
          <v-col cols="12" md="6" class="pr-md-8">
            <h2 class="my-6">Allgemeine Hinweise
              <v-divider></v-divider>
            </h2>
            <ul class="ml-12 my-2">
              <li>Die Tests sollten <b>regelmäßig und vollständig</b> absolviert werden, um eine kontinuierliche
                Datenerhebung sicherzustellen und die Berechnung eines gültigen Composite Scores zu gewährleisten.
              </li>
              <li>Patient:innen sollten sich genügend Zeit nehmen und die Tests <b>gewissenhaft und pünktlich</b> in der
                neotivCare-App durchführen.
              </li>
              <li>Eine ruhige Umgebung ist entscheidend: Die Tests sollten in einem <b>ablenkungsfreien Raum</b>
                durchgeführt werden.
              </li>
            </ul>
          </v-col>

          <!-- Right Column -->
          <v-col cols="12" md="6" class="pl-md-8">
            <h2 class="my-6">Tipps zur Vorbereitung auf die Tests
              <v-divider></v-divider>
            </h2>
            <ul class="ml-12 my-2">
              <li>Der Geräte-Bildschirm der Patient:innen sollte ausreichend hell sein, um die Testbilder klar zu
                erkennen. Falls nötig, sollte die <b>Bildschirm-Helligkeit</b> angepasst werden.
              </li>
              <li>Falls eine <b>Sehhilfe</b> benötigt wird, sollte diese unbedingt während der Tests von den
                Patient:innen getragen werden.
              </li>
            </ul>
          </v-col>
        </v-row>

        <h2 class="my-6">Tipps zur Durchführung der Tests
          <v-divider></v-divider>
        </h2>
        <ul class="mb-4">
          <li>Jeder Gedächtnistest besteht aus zwei Teilen, mit einer kurzen Pause dazwischen. Die gesamte benötigte
            Testzeit wird in der App angezeigt.
          </li>
          <li>Die App erinnert die Patient:innen automatisch an bevorstehende Tests.</li>
          <li>Beide Testteile müssen innerhalb der angegebenen Zeit abgeschlossen werden, da sonst keine Auswertung der
            Daten erfolgen kann.
          </li>
          <li>Nach Abschluss eines Tests sollten die Patient:innen zum Hauptbildschirm der App zurückkehren, um den
            nächsten Testzeitpunkt einsehen zu können.
          </li>
        </ul>
        <p> Weitere wichtige Hinweise finden Sie unter <a @click="scrollToSection('.frequent-questions')"
                                                          class="anchor-link">FAQs</a></p>
      </v-container>
    </section>

    <!-- Materials for download -->
    <section class="bg-feature-orange">

      <v-container>
        <v-row>
          <v-col cols="12">
            <h1 class="text-left my-6">Materialien zum Download
              <v-divider></v-divider>
            </h1>

          </v-col>
          <v-col cols="12" sm="12">
            <p><b>Hier finden Sie alle Materialien für die Refer-MCI Studie als Download:</b></p>
            <p>Dokumente der Patient:innen-Mappe:</p>
            <ul class="mb-4">

              <li><a href="/static/pdfs/refermci/Ubersicht_zur_Patientinnen‑Mappe.pdf"
                     target="_blank">Übersicht zur Patient:innen‑Mappe</a></li>

              <li><a
                href="/static/pdfs/refermci/Teilnehmerinformation_undTeilnahmeerklarung_Arztpraxis_und_Gedachtnisambulanz.pdf"
                target="_blank">Teilnehmerinformation & Teilnahmeerklärung (Arztpraxis & Gedächtnisambulanz)</a></li>

              <li><a href="/static/pdfs/refermci/Dokumentationsbogen_Routineversorgung.pdf"
                     target="_blank">Dokumentationsbogen Routineversorgung</a></li>

              <li><a href="/static/pdfs/refermci/Bogen_Anamnese_und_Diagnostik1.pdf"
                     target="_blank"> Bogen Anamnese und Diagnostik (1)</a></li>

              <li><a href="/static/pdfs/refermci/Patient-innen‑Broschure.pdf"
                     target="_blank">Patient:innen‑Broschüre</a></li>

              <li><a href="/static/pdfs/refermci/Installationsanleitung.pdf"
                     target="_blank">Installationsanleitung</a></li>

              <li><a href="/static/pdfs/refermci/1.Fragebogen_Ihre_Teilnahme_an_der_Refer‑MCI‑Studie.pdf"
                     target="_blank">1. Fragebogen „Ihre Teilnahme an der Refer‑MCI‑Studie“</a></li>

              <li><a href="/static/pdfs/refermci/Bogen_Anamnese_und_Diagnostik2.pdf"
                     target="_blank">Bogen Anamnese und Diagnostik (2)</a></li>

              <li><a
                href="/static/pdfs/refermci/2.Fragebogen_zur_Nutzerfreundlichkeit_und_zum_Mehrwert_der_neotivCare‑App.pdf"
                target="_blank">2. Fragebogen zur Nutzerfreundlichkeit und zum Mehrwert der neotivCare</a></li>
            </ul>

            <p>Für das ärztliche Personal:</p>
            <ul class="mb-4">
              <li><a href="/static/pdfs/refermci/Handreichung_fur_den_Ablauf_in_der_Praxis.pdf"
                     target="_blank">Handreichung für den Ablauf in der Praxis (kann auch als Checkliste genutzt
                werden)</a></li>

              <li><a href="/static/pdfs/refermci/Fachbroschure.pdf"
                     target="_blank">Fachbroschüre</a></li>

              <li><a href="/static/pdfs/refermci/Beispielbefundbrief.pdf"
                     target="_blank">Beispielbefundbrief</a></li>

              <li><a href="/static/pdfs/refermci/Entscheidungsbaumhilfe.pdf"
                     target="_blank">Entscheidungsbaum/hilfe</a></li>

              <li><a href="/static/pdfs/refermci/Patientenflyer_zur_Auslage_in_der_Praxis_falls_gewunscht.pdf"
                     target="_blank">Patientenflyer (zur Auslage in der Praxis, falls gewünscht) </a></li>
            </ul>
          </v-col>
        </v-row>
      </v-container>
    </section>


    <!-- Support -->
    <section class="support bg-white">
      <v-container>
        <h1 class="text-left my-6">
          Support
          <v-divider></v-divider>
        </h1>
        <h2 class="mb-4">Technischer Support & Datenschutz
          <v-divider></v-divider>
        </h2>
        <p>Teilnehmende Patient:innen können technische Probleme jederzeit direkt in der neotivCare-App
          melden. Dies geschieht über: </p>
        <p>Menü > Hilfe > Ein Problem melden </p>
        <p>Diese Meldungen werden an das Support Team von neotiv geschickt und enthalten bestimmte
          technische Metadaten (z. B. Gerätetyp, Betriebssystem). Dadurch kann das Support-Team Probleme effizient
          analysieren und beheben. </p>
        <ul class="mb-4">
          <li>Die Problemmeldung des Nutzers wird über deren private E-Mail-Adresse an das neotiv Support System
            gesendet
          </li>
          <li>Das neotiv Support Team kann einen direkten Kontakt zu den entsprechenden Personen herstellen und so
            Lösungsmöglichkeiten kommunizieren
          </li>
          <li>Alle erfassten Daten dienen ausschließlich der Fehlerbehebung und werden nicht für andere Zwecke
            genutzt.
          </li>
        </ul>

        <p>Die neotivCare-App entspricht höchsten Sicherheitsstandards: </p>
        <ul class="mb-4">
          <li><b>Sichere Datenübertragung:</b> Alle Daten werden über eine verschlüsselte TLS 1.2-Verbindung nach
            NIST-Standard übertragen.
          </li>
          <li><b>Datenspeicherung:</b> Alle App-Daten werden auf Servern in Deutschland gespeichert und täglich
            verschlüsselt gesichert.
          </li>
          <li><b>Offline-Datenspeicherung:</b> Falls keine Internetverbindung besteht, bleiben die Daten verschlüsselt
            auf dem Gerät gespeichert und werden automatisch übertragen, sobald die Verbindung wiederhergestellt ist.
          </li>
          <li><b>Externe Sicherheitsprüfung:</b> neotiv hat ein externes Penetration Testing durch AuraSec Ltd mit sehr
            guten Ergebnissen bestanden.
          </li>
        </ul>

        <h2 class="mb-4 anchor-section frequent-questions">Häufig gestellte Fragen (FAQ)
          <v-divider></v-divider>
        </h2>
        <p>Hier finden Sie Antworten auf häufig gestellte Fragen zur neotivCare-App. </p>


        <!--FAQs-->
        <v-expansion-panels class="mb-4">
          <v-expansion-panel
            title="Wie funktioniert die Erstellung eines Benutzerkontos & Authentifizierung? "
          >
            <v-expansion-panel-text>
              <ul>
                <li><b>Benutzerkonto:</b>
                  <ul>
                    <li>Für die erstmalige Nutzung von neotivCare muss im ersten Schritt ein Benutzerkonto angelegt
                      werden.
                    </li>
                    <li>Dazu gibt der Patient bzw. die Patientin in der App zuerst den Freischaltcode ein, den Sie vom
                      Facharzt bzw. der Fachärztin erhalten.
                    </li>
                    <li>Nachdem der gültige Freischaltcode eingegeben wurde, werden einige persönliche Informationen
                      abgefragt, welche für den Befundbrief genutzt werden.
                    </li>
                  </ul>
                </li>


                <li><b>Authentifizierung:</b>
                  <ul>
                    <li>Nachdem ein Benutzerkonto angelegt wurde, muss dieses bestätigt werden. Dies kann auf 2
                      verschiedene
                      Weisen geschehen:
                    </li>
                    <li>Mit der E-Mail-Adresse Registrieren</li>
                    <li>Wenn Sie regelmäßig Ihre Emails nutzen, ist dies eine praktische Art Ihr Benutzerkonto zu
                      bestätigen.
                    </li>
                    <li>Geben Sie dazu die E-Mail-Adresse ein und setzen Sie im nächsten Schritt Ihr Passwort.</li>
                    <li>An die eingetragene E-Mail-Adresse wir nun automatisch eine E-Mail von neotiv mit einem
                      Bestätigungslink verschickt. Bitte schauen Sie in Ihrem privaten E-Mail-Postfach nach und
                      bestätigen
                      somit Ihr neotivCare-Benutzerkonto.
                    </li>
                  </ul>
                </li>

              </ul>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            title="Was ist der Freischaltcode?"
          >
            <v-expansion-panel-text>
              <ul>
                <li>Bei dem Freischaltcode handelt es sich um einen 16-stelligen Code, welchen die Patienten bzw.
                  Patientinnen für die Nutzung der neotivCare-App benötigen. Den Freischaltcode erhalten Sie von neotiv
                  und händigen diesen an die Patient:innen aus.
                </li>
                <li>Jeder Freischaltcode ist nur einmal gültig. Der Freischaltcode kann nicht mehrfach in der App
                  eingegeben oder für verschiedene Testungen verwendet werden.
                </li>
              </ul>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            title="Der Patient:in erhält keine Push-Benachrichtigungen – was tun?"
          >
            <v-expansion-panel-text>
              <ul>
                <li>Zu Beginn der Registrierung in der neotivCare-App wird der Patient bzw. die Patientin gefragt, ob
                  Push-Benachrichtigungen zugelassen werden sollen – dem sollen immer zugestimmt werden.
                </li>
                <li>Sollten dennoch keine Benachrichtigungen erhalten werden, können in den App Einstellungen (zu finden
                  im Menü Punkt „Einstellungen“ des Geräts) die Benachrichtigungen der neotivCare-App überprüft werden.
                </li>
                <li>Lässt sich das Problem dennoch nicht lösen, bitten wir Sie, den Patienten bzw. die Patientin
                  aufzufordern, eine Problemmeldung über die neotivCare-App zu verschicken.
                </li>
              </ul>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            title="Können die Testungen unterbrochen werden? "
          >
            <v-expansion-panel-text>
              <ul>
                <li>Die Tests von neotivCare bestehen alle aus 2 Teilen. Beide Teile sind durch eine Pause getrennt.
                  Wichtig ist, dass die Testteile in den vorgegebenen Zeiten gestartet und beendet werden. Diese Zeiten
                  werden in der App angegeben.
                </li>
                <li>Des Weiteren bietet die App eine Pausen Option während jeder Testung. Bis zu 2 Minuten kann die
                  laufende Testung unterbrochen werden.
                </li>
              </ul>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            title="Was passiert, wenn ein Test verpasst wird?"
          >
            <v-expansion-panel-text>
              <ul>
                <li>Die 3 verschiedenen Tests werden jeweils 4-mal durchgeführt. Für die Berechnung eines
                  Gesamtergebnisses (Composite Score) werden mindestens 2 gültige Testergebnisse pro Test benötigt. Das
                  bedeutet, dass ein verpasster Test die Berechnung eines Gesamtergebnisses nicht verhindert. Werden
                  allerdings zu viele Tests nicht durchgeführt, kann es dazu führen, dass kein Gesamtergebnis (Composite
                  Score) berechnet werden kann nach den 12 Testungen.
                </li>
              </ul>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            title="Wie generiere ich den Befundbrief?"
          >
            <v-expansion-panel-text>
              <ul>
                <li>Sobald eine ausreichende Anzahl an gültigen Testergebnissen verfügbar ist, kann der Composite Score
                  innerhalb des Befundbriefs eingesehen werden.<br>

                  Innerhalb der neotivCare App im „Menü“ unter „Befundbrief“ kann der Befundbrief generiert werden. Dies
                  kann von den Patient:innen selbstständig in der App gemacht werden. Nach Eingabe der nötigen Daten
                  (Name, Vorname, Geburtsdatum, Geschlecht) erfolgt ein Sicherheitsschritt, in dem ein Sicherheitscode
                  eingegeben werden muss, der an die E-Mail Adresse des neotivCare Benutzerkontos der Patient:innen
                  geschickt wird. Hier gilt es die Zeitbegrenzung von 5 Minuten einzuhalten, in denen der
                  Sicherheitscode gültig ist. Nach dem erfolgreichem Sicherheitsschritt kann der Befundbrief in der
                  neotivCare geöffnet werden.<br>

                  Sie können dann den Befundbrief direkt von der neotivCare App aus an Ihre zuständige Praxis schicken.
                  Die Email Adresse der Praxis ist in unserem System hinterlegt, sodass Ihr Befundbrief mit einem Klick
                  direkt an die entsprechende Arztpraxis versendet wird.
                  <br>

                  Außerdem kann der Befundbrief an die eigene E-Mail-Adresse des
                  Patienten/der Patientin geschickt werden.<br>

                  Dieser lässt sich zur Besprechung in der Praxis per E-Mail weiterleiten (bspw. and die Praxis
                  Email-Adresse) oder ausgedruckt von den Patient:innen zur Befundbesprechung in der Praxis mitbringen.
                </li>

              </ul>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            title="Was machen bei einem technischen Problem?"
          >
            <v-expansion-panel-text>
              <ul>
                <li>Alle Patienten und Patientinnen könne über die neotivCare App unseren hauseigenen Kundenservice
                  erreichen. Dies sollte für technische Probleme aller Art die erste Anlaufstelle sein. Sollten sich
                  Patient:innen bei Ihnen mit technischen Problemen melden, verweisen Sie sie gerne auf diese Funktion
                  innerhalb der App. Alternativ können Sie die untenstehenden Kontaktdaten an die entsprechende Person
                  weitergeben.
                </li>
              </ul>
            </v-expansion-panel-text>
          </v-expansion-panel>

        </v-expansion-panels>
      </v-container>
    </section>

    <section class="bg-light-orange">
      <v-container>
        <h2 class="mb-4">Ihr Kontakt für Fragen aller Art</h2>

        <p>Bei technischen Fragen oder Problemen sowie bei Unklarheiten zur Studie, schreiben Sie uns eine E-Mail
          an:</p>

        <p><b>Projektleiter Dr. Hannes Voßfeldt</b></p>
        <p>E-Mail: <a href="mailto:hannes.vossfeldt@neotiv.com">hannes.vossfeldt@neotiv.com</a></p>
        <p>Mobilnummer: <a href="tel:+491711792278">0171 179 2278 </a></p>

        <p class="mb-0">Hotline (24h): <a href="tel:+49039158245026">+49 (0)391 58245026</a></p>
        <p>(Bandansage mit Erstellung eines Tickets und Rückruf innerhalb von 48h)</p>
      </v-container>
    </section>

    <!-- Contact form -->
    <section class="contact bg-orange-primary">
      <v-container class="py-12">
        <v-container>
          <h3 class="text-white text-center">
            Contact for Support
          </h3>
          <p class="text-white mt-7">
            Oder schreiben Sie unserem Support Team im folgenden Kontaktformular.
          </p>
        </v-container>
        <contact-form
          :formColor="'#ED6D41'"
          :formBtnTextColor="'#ED6D41'"
          :formBtnColor="'#FBEEE7'"
          :newsletter="false"
        />
      </v-container>
    </section>


  </v-container>
</template>

<script>
import {nextTick} from 'vue'
import ContactForm from '@/components/Form.vue'

export default {
  components: {ContactForm},

  data() {
    return {
      activeVideo: '',
      dialog: false,
      tab: 'Praxis'
    }
  },
  methods: {
    scrollToSection(selector) {


      nextTick(() => {
        setTimeout(() => {
          const element = document.querySelector(selector)
          element.scrollIntoView({alignToTop: true, block: 'start', behavior: 'smooth'})
        }, 300)
      });
    }
  }
};
</script>

<style scoped>

.anchor-section {
  scroll-margin-top: 110px;
}

.hero-title {
  font-size: 40px;
  line-height: 1.25;
}

.hero-text {
  font-size: 24px;
  line-height: 1.25;
  color: #ED6D41;
}

.bg-orange-primary {
  background-color: #ED6D41;
}

.bg-light-orange {
  background-color: #FBEEE7;
}

.bg-feature-orange {
  background-color: #F4D1BE;
}

:deep(.v-expansion-panel-title) {
  font-size: 18px;
}

p {
  margin-bottom: 1rem;
}

a, .text-orange-primary {
  color: #ED6D41;
}

.text-footnote {
  font-size: 16px;
}

ul, ol {
  margin-left: 2.5rem;
  line-height: 1.75em;
}

@media only screen and (max-width: 600px) {
  .hero-title {
    font-size: 30px;
  }
}

h1 {
  font-size: 38px;
}

body {
  font-family: 'Source Sans', sans-serif;
}

table, th, td {
  border: 1px solid #CCCCCC;
  border-collapse: collapse;
}

th, td {
  padding: 0.3rem 1rem;
}

.anchor-link {
  cursor: pointer;
}

.img-border {
  border: 1px solid #CCCCCC;
}
</style>
